import React from "react";
import "./styles.css";
import AnimateHeight from "react-animate-height";
import PatientPlansSubList from "../PatientPlansSubList";
import {
    CONTAINER_HEIGHT,
    LIST_ITEM_HEIGHT,
    N_A,
    lockedPlanTooltipMsg,
    patientListPlanStageTooltip,
} from "../constants";
import PropTypes from "prop-types";
import { EMPTY_STRING, N_A_STRING } from "../../../constants";
import LockIcon from "../../../ui/icons/LockIcon";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import FlagIcon from "../../../ui/icons/FlagIcon";
import {
    planStageEnum,
    planStageTooltipMsg,
} from "../../__shared__/RequirementsModal/constants";
import { planStageToText } from "../helpers";

const CLOSED = LIST_ITEM_HEIGHT;
const TOGGLE_THRESHOLD = 5; //Number of plans required to display sorting toggles

class PatientListItem extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            planBox: CLOSED,
        };

        this.openHeight = LIST_ITEM_HEIGHT + CONTAINER_HEIGHT;
        this.handleClick = this.handleClick.bind(this);
        this.setHeight = this.setHeight.bind(this);
        this.getLockHolder = this.getLockHolder.bind(this);
    }

    setHeight(props) {
        let numRows =
            props.plansData.length >= TOGGLE_THRESHOLD
                ? TOGGLE_THRESHOLD + 1
                : props.plansData.length;
        this.openHeight = LIST_ITEM_HEIGHT + numRows * LIST_ITEM_HEIGHT;
    }

    componentDidMount() {
        this.setHeight(this.props);
    }

    componentDidUpdate(prevProps) {
        this.setHeight(this.props);

        if (this.props.toggleClose !== prevProps.toggleClose) {
            this.setState({ planBox: CLOSED });
        }
    }

    handleClick() {
        this.setState({
            planBox: this.state.planBox === CLOSED ? this.openHeight : CLOSED,
        });
    }

    getLockHolder() {
        const { patientData, plansData, userData } = this.props;

        if (!patientData || !plansData || plansData.length === 0) {
            return null;
        }

        const targetPlan = patientData.find(
            (patientPlan) =>
                patientPlan["MetaData"]["PlanUUID"] === plansData[0]["planUuid"]
        );

        const locker = targetPlan?.["LockedByUser"];
        if (locker && locker["uuid"] !== userData.uuid) {
            return locker;
        }
        return null;
    }

    renderLockTooltip(locker) {
        return (
            <Tooltip id="patient-list-item-tooltip">
                {lockedPlanTooltipMsg(
                    locker["firstName"],
                    locker["lastName"],
                    locker["email"]
                )}
            </Tooltip>
        );
    }

    renderFlagTooltip() {
        return (
            <Tooltip id="patient-list-item-flag-tooltip">
                {this.props?.planFlag}
            </Tooltip>
        );
    }

    renderTooltip() {
        const { patientData, plansData, planStage } = this.props;

        if (!patientData || !plansData || plansData.length === 0) {
            return null;
        }

        const targetPlan = patientData.find(
            (patientPlan) =>
                patientPlan["MetaData"]["PlanUUID"] === plansData[0]["planUuid"]
        );
        const planStageLastModifiedDate =
            targetPlan["MetaData"]["PlanStageModifiedDate"];

        let tooltipMsg = `${planStageTooltipMsg[planStage]} `;
        tooltipMsg += planStageLastModifiedDate
            ? planStageLastModifiedDate
            : N_A_STRING;

        if (planStage === planStageEnum.NEEDS_DATA) {
            if (
                !targetPlan["SavedData"]?.IsPathReportUploaded &&
                !targetPlan["SavedData"]?.PSA
            ) {
                tooltipMsg = patientListPlanStageTooltip.MISSING_ALL;
            } else if (!targetPlan["SavedData"]?.IsPathReportUploaded) {
                tooltipMsg = patientListPlanStageTooltip.MISSING_PATH;
            } else if (!targetPlan["SavedData"]?.PSA) {
                tooltipMsg = patientListPlanStageTooltip.MISSING_PSA;
            }
        }

        return (
            <Tooltip id="patient-list-item-flag-tooltip">{tooltipMsg}</Tooltip>
        );
    }

    render() {
        let inProgColor = this.props.inProgress
            ? "text-primary-500"
            : EMPTY_STRING;
        let className = `patient-list-item${
            this.state.planBox === this.openHeight
                ? " selectedItem"
                : EMPTY_STRING
        }`;

        const lockHolder = this.getLockHolder();
        const isLocked = lockHolder !== null;
        const lockTooltip = isLocked
            ? this.renderLockTooltip(lockHolder)
            : null;

        return (
            <AnimateHeight
                id={this.props.listItemId}
                className={"anim-spec"}
                height={this.state.planBox}
                animateOpacity
            >
                <div
                    className={className}
                    style={this.props.style}
                    onClick={this.handleClick}
                >
                    <div id="patient-flag-box">
                        <p className={`${inProgColor} pendo-ignore m-0`}>
                            {this.props.planFlag && (
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={this.renderFlagTooltip()}
                                >
                                    <div>
                                        <FlagIcon />
                                    </div>
                                </OverlayTrigger>
                            )}
                        </p>
                    </div>
                    <div className="patient-id-box">
                        <p className={`text-lg ${inProgColor} pendo-ignore`}>
                            {this.props.id}
                        </p>
                    </div>
                    <div className="patient-name-box flex items-center">
                        <p className={`text-lg ${inProgColor} pendo-ignore`}>
                            {this.props.name}
                        </p>
                    </div>
                    <div className="patient-date-box flex items-center">
                        <p className={`text-lg ${inProgColor} pendo-ignore`}>
                            {this.props.creationDate}
                        </p>
                    </div>
                    <div
                        className="patient-plan-stage-box"
                        id={`plan-stage-item-${this.props.patientIndex}`}
                    >
                        {this.props?.planStage > planStageEnum.NO_STATUS ? (
                            <ul
                                id="plan-status-badge"
                                className="patient-plan-stage-badge"
                            >
                                <li>
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={this.renderTooltip()}
                                    >
                                        <p className={`${inProgColor}`}>
                                            {planStageToText(
                                                this.props.planStage
                                            )}
                                        </p>
                                    </OverlayTrigger>
                                </li>
                            </ul>
                        ) : null}
                    </div>
                    {isLocked && (
                        <OverlayTrigger placement="right" overlay={lockTooltip}>
                            <div className="patient-lock-icon">
                                <LockIcon />
                            </div>
                        </OverlayTrigger>
                    )}
                </div>
                <PatientPlansSubList
                    listItemId={this.props.listItemId}
                    plansData={this.props.plansData}
                    clickCallback={this.props.clickCallback}
                    checkNotification={this.props.checkNotification}
                    userData={this.props.userData}
                    authToken={this.props.authToken}
                    isLocked={isLocked}
                    tooltipMsg={lockTooltip}
                />
            </AnimateHeight>
        );
    }
}

PatientListItem.propTypes = {
    //Data for all plans associated with a single patient
    plansData: PropTypes.arrayOf(PropTypes.object),
    patientData: PropTypes.array,
    //Toggle to close the Plan sub list container so patients can be sorted
    toggleClose: PropTypes.bool,
    //ID for the list item
    listItemId: PropTypes.string,
    patientIndex: PropTypes.number,
    //Name of Patient
    name: PropTypes.string,
    //Patient MRN
    id: PropTypes.string,
    creationDate: PropTypes.string,
    planStage: PropTypes.number,
    //Function to callback from plan Download Icon click
    clickCallback: PropTypes.func,
    //Loading in progress
    inProgress: PropTypes.bool,
    style: PropTypes.object,
    checkNotification: PropTypes.func,
    userData: PropTypes.object,
    authToken: PropTypes.string,
    planFlag: PropTypes.string,
};

PatientListItem.defaultProps = {
    plansData: [],
    toggleClose: false,
    name: N_A,
    id: N_A,
    creationDate: N_A,
    planStage: planStageEnum.NO_STATUS,
    inProgress: false,
    userData: {},
    authToken: EMPTY_STRING,
};

const mapStateToProps = function (state) {
    return {
        authToken: state.LoginReducer.authToken,
        userData: state.LoginReducer.userData,
    };
};

export default withRouter(connect(mapStateToProps)(PatientListItem));
